import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, nextDocNo, parse, requireRole, roleCodesOf, txLog } from './c07.shared'
import { ST } from './destructionRm.handler'

/**
 * Endpoint custom C14 NG Handling RM — `/api/v1/wms/ng-handling/*` (DSR-03 Tugas Pemindahan Barang NG, Mobile Scan):
 * - `tasks`    : tugas pemindahan yang masih terbuka, diturunkan dari `rm_ng_item` (tidak ada tabel tugas — ERD hanya punya
 *                `rm_ng_item.{move_task_no, move_operator_id}`; kolom `destroy_task_no/destroy_operator_id` = [ERD-GAP], laporan 18).
 *                Dua kaki: `TO_ZONE` (rak → ZNG-RM; task `TNG-…` dari INV-01 "Tugaskan Pemindahan") dan
 *                `TO_DESTRUCTION` (ZNG-RM → ZMS-RM; task `PMR-…` dari DSR-02 "Terbitkan Tugas Pindah").
 * - `complete` : scan QR area tujuan SEKALI = seluruh perpindahan tertulis (prototype `simpanPindah`). Barang yang
 *                dipindah → `MOVED` (kaki 1) / `AT_DESTRUCTION` (kaki 2, membuka tombol "Tandai Selesai" BAP-RM di C12).
 */

const OPERATOR = 'operator_wh_rm'
const ZONE_CODE = 'ZNG-RM'
const DESTRUCTION_CODE = 'ZMS-RM'
export const NG_LEG = { toZone: 'TO_ZONE', toDestruction: 'TO_DESTRUCTION' } as const
type Leg = (typeof NG_LEG)[keyof typeof NG_LEG]

type NgLog = { at: string; ev: string; by: string | null }
const num = (value: unknown) => Number(value ?? 0)
const iso = () => new Date().toISOString()
const stamp = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 16).replace('T', ' ')
const logOf = (item: LocalRow) => ((item.log as NgLog[] | undefined) ?? []).slice()
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}

const LEG_META: Record<Leg, { target: string; targetName: string; gate: string | null; status: string }> = {
  TO_ZONE: { target: ZONE_CODE, targetName: 'Area Not Good', gate: null, status: 'MOVING' },
  TO_DESTRUCTION: {
    target: DESTRUCTION_CODE,
    targetName: 'Area Pemusnahan',
    gate: ZONE_CODE,
    status: ST.moving
  }
}

/** Kaki + nomor tugas + petugas sebuah baris, atau `null` bila baris tidak sedang dalam tugas pemindahan. */
function taskOf(item: LocalRow): { leg: Leg; task_no: string; operator_id: string } | null {
  if (item.item_status === LEG_META.TO_ZONE.status && item.move_task_no)
    return { leg: NG_LEG.toZone, task_no: String(item.move_task_no), operator_id: idOf(item.move_operator_id) ?? '' }
  if (item.item_status === LEG_META.TO_DESTRUCTION.status && item.destroy_task_no)
    return {
      leg: NG_LEG.toDestruction,
      task_no: String(item.destroy_task_no),
      operator_id: idOf(item.destroy_operator_id) ?? ''
    }
  return null
}

function lineOf(item: LocalRow, leg: Leg) {
  const material = findRow('mt_material', idOf(item.material_id))
  const bin = findRow('mt_rack_bin', idOf(item.rack_bin_id))
  const reasonIds = (item.reason_ids as string[] | undefined) ?? (item.reason_id ? [String(item.reason_id)] : [])
  return {
    id: String(item.id),
    ngz_no: String(item.ngz_no ?? ''),
    sticker_ngq: String(item.sticker_ngq ?? ''),
    qr_code: String(item.qr_code ?? ''),
    material_code: String(material?.code ?? ''),
    material_name: String(material?.name ?? ''),
    batch_no: String(item.batch_no ?? ''),
    qty: num(item.qty),
    unit_name: String(item.unit_name ?? ''),
    reasons: reasonIds.map(id => String(findRow('mt_reject_type', id)?.name ?? '')).filter(Boolean),
    from_location: leg === NG_LEG.toDestruction ? ZONE_CODE : String(bin?.code ?? '') || null
  }
}

/** Nama material + qty per satuan, mis. `12 Sak + 3 Roll` (prototype `teksJml`). */
export function unitTotals(lines: { qty: number; unit_name: string }[]) {
  const sums = new Map<string, number>()
  lines.forEach(row => sums.set(row.unit_name || 'unit', (sums.get(row.unit_name || 'unit') ?? 0) + row.qty))
  return [...sums.entries()].map(([unit, qty]) => ({ unit, qty }))
}

export function tasks() {
  const me = currentUserId()
  const isOperator = roleCodesOf(me).includes(OPERATOR)
  const groups = new Map<string, { leg: Leg; task_no: string; operator_id: string; rows: LocalRow[] }>()
  readTable('rm_ng_item').forEach(item => {
    const task = taskOf(item)
    if (!task) return
    const group = groups.get(task.task_no) ?? { ...task, rows: [] }
    group.rows.push(item)
    groups.set(task.task_no, group)
  })
  return [...groups.values()]
    .filter(group => !isOperator || group.operator_id === me)
    .map(group => {
      const meta = LEG_META[group.leg]
      const lines = group.rows
        .map(row => lineOf(row, group.leg))
        .sort((a, b) => a.sticker_ngq.localeCompare(b.sticker_ngq))
      const doc = group.rows[0].destruction_id ? findRow('rm_destruction', idOf(group.rows[0].destruction_id)) : null
      return {
        id: group.task_no,
        task_no: group.task_no,
        leg: group.leg,
        operator_id: group.operator_id,
        operator_name: userName(group.operator_id),
        mine: group.operator_id === me,
        status: 'ASSIGNED' as const,
        target_code: meta.target,
        target_name: meta.targetName,
        gate_code: meta.gate,
        bap_no: doc ? String(doc.bap_no) : null,
        line_count: lines.length,
        total_planned: lines.reduce((sum, row) => sum + row.qty, 0),
        totals: unitTotals(lines),
        lines
      }
    })
    .sort((a, b) => a.task_no.localeCompare(b.task_no))
}

type Body = { task_no?: string; area_code?: string; lines?: { id: string; qty: number }[]; note?: string }

/** Pemindahan sebagian: baris dipecah, bagian yang dibawa dipindah dan sisanya tetap menggantung (prototype `simpanPindah`). */
export function complete(body: Body) {
  requireRole('Pemindahan hanya bisa dilakukan operator gudang RM.', OPERATOR)
  const me = currentUserId()
  const taskNo = String(body.task_no ?? '')
  const rows = readTable('rm_ng_item').filter(item => taskOf(item)?.task_no === taskNo)
  if (!rows.length) fail('Tugas ini sudah selesai atau tidak ada — muat ulang layarnya.', 'ERR_STATE_CONFLICT', 409)
  const { leg, operator_id } = taskOf(rows[0]) as NonNullable<ReturnType<typeof taskOf>>
  if (operator_id !== me) fail(`Tugas ini milik ${userName(operator_id)}.`, 'ERR_UNAUTHORIZED_ROLE', 403)
  const meta = LEG_META[leg]
  const carried = (body.lines ?? []).filter(line => line && line.id)
  if (!carried.length) fail('Belum ada barang yang diangkat.')
  if (new Set(carried.map(line => line.id)).size !== carried.length) fail('Satu barang hanya boleh muncul sekali.')
  carried.forEach(line => {
    const row = rows.find(item => item.id === line.id)
    if (!row) fail('Barang bukan bagian tugas ini.', 'ERR_VALIDATION_FAILED')
    const qty = num(line.qty)
    if (!(qty >= 1)) fail('Isi jumlahnya dulu.', 'ERR_VALIDATION_FAILED')
    if (qty > num(row?.qty)) fail(`Melebihi jumlah yang tercatat (${num(row?.qty)})`, 'ERR_VALIDATION_FAILED')
  })
  if (
    String(body.area_code ?? '')
      .trim()
      .toUpperCase() !== meta.target
  )
    fail(
      `Area tujuan salah. Barang yang dibawa harus diletakkan di ${meta.targetName} (${meta.target}). Belum ada yang dipindah.`,
      'ERR_VALIDATION_FAILED'
    )
  // Catatan wajib hanya bila ada barang yang sama sekali tidak diangkat (prototype `jaga`); kurang jumlah cukup dilog.
  const leftover = rows.some(row => !carried.some(item => item.id === row.id))
  const note = String(body.note ?? '').trim()
  if (leftover && !note) fail('Catatan wajib bila ada barang yang belum diangkat.', 'ERR_VALIDATION_FAILED')

  const at = stamp()
  const user = currentUserId()
  const movedStatus = leg === NG_LEG.toZone ? 'MOVED' : ST.atArea
  let moved = 0
  carried.forEach(line => {
    const row = rows.find(item => item.id === line.id) as LocalRow
    const qty = num(line.qty)
    const rest = num(row.qty) - qty
    moved += qty
    if (rest > 0) {
      // Sisa menggantung: kaki 1 kembali ke antrean pemindahan, kaki 2 tetap di BAP & tugas yang sama (tugas tetap terbuka).
      insertRow('rm_ng_item', {
        ...row,
        ...auditFieldsForCreate(),
        id: newId(),
        ngz_no: nextDocNo('NGZ', 'rm_ng_item', 'ngz_no'),
        qty: rest,
        ...(leg === NG_LEG.toZone
          ? { item_status: 'AWAITING_MOVE', move_operator_id: null, move_task_no: null }
          : { item_status: ST.moving }),
        log: [
          ...logOf(row),
          {
            at,
            ev: `Sisa ${rest} ${String(row.unit_name ?? '')} belum ikut dipindah (${taskNo})${note ? `: ${note}` : ''}`,
            by: user
          }
        ]
      })
    }
    updateRow('rm_ng_item', String(row.id), {
      qty,
      item_status: movedStatus,
      moved_at: iso(),
      moved_by_id: user,
      log: [
        ...logOf(row),
        {
          at,
          ev: `Dipindah ${qty} ${String(row.unit_name ?? '')} ke ${meta.target} (QR area discan, tugas ${taskNo})${note ? ` — catatan: ${note}` : ''}`,
          by: user
        }
      ],
      ...auditFieldsForUpdate()
    })
  })
  // Baris yang tidak diangkat sama sekali: kaki 1 kembali ke antrean; kaki 2 tetap menunggu di tugas yang sama.
  if (leg === NG_LEG.toZone)
    rows
      .filter(row => !carried.some(line => line.id === row.id))
      .forEach(row =>
        updateRow('rm_ng_item', String(row.id), {
          item_status: 'AWAITING_MOVE',
          move_operator_id: null,
          move_task_no: null,
          log: [...logOf(row), { at, ev: `Tidak ikut diangkat di ${taskNo}: ${note}`, by: user }],
          ...auditFieldsForUpdate()
        })
      )
  const open = readTable('rm_ng_item').filter(item => taskOf(item)?.task_no === taskNo).length
  const bapId = rows[0].destruction_id ? idOf(rows[0].destruction_id) : null
  const bap = bapId ? findRow('rm_destruction', bapId) : null
  const docReady = bapId
    ? readTable('rm_ng_item')
        .filter(item => idOf(item.destruction_id) === bapId)
        .every(item => item.item_status === ST.atArea)
    : false
  txLog(
    'RM NG',
    'DSR-03',
    'pindah-ng',
    taskNo,
    `${carried.length} barang, ${moved} unit dipindah ke ${meta.target}${note ? ` [CATATAN: ${note}]` : ''}`
  )
  return {
    task_no: taskNo,
    leg,
    target_code: meta.target,
    moved_lines: carried.length,
    moved_qty: moved,
    open_lines: open,
    bap_no: bap ? String(bap.bap_no) : null,
    doc_ready: docReady
  }
}

export function registerNgHandlingHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/ng-handling'
  mock.onGet(`${base}/tasks`).reply(() => handle(() => tasks()))
  mock.onPost(`${base}/complete`).reply(config => handle(() => complete(parse<Body>(config.data))))
}
